'use client'

import { ComponentType, createContext, ReactNode, useContext } from 'react'

import { SeoListName } from 'shared/analytics'
import { ButtonVariants } from 'shared/ui/Button/Button.enums'
import { isProd } from 'shared/utils/env'

import type { AnyProductTypes, ProductTypes } from './types/products'

export interface ProductActionButtonProps {
  product: AnyProductTypes
  seoButtonPlace: string
  idxOnList: number
  listName: SeoListName
  quantityJump?: number
  size?: 'default' | 'large' | 'small'
  disabled?: boolean | number
  isCollapsible?: boolean
  variant?: ButtonVariants
  icon?: string
  handleSyneriseRecommendationClickEvent?: () => void
  algoliaInsightsAttributes?: Record<string, string | boolean | number>
}

export interface SpecialPromotionProps {
  product: AnyProductTypes
  source?: string
  notClickable?: boolean
}

// LR: the substitutes modal renders product cards that replace a cart item.
export interface ProductReplaceButtonProps {
  substitute: ProductTypes
  realProductId?: string
  idxOnList: number
  quantityJump?: number
}

export interface ProductPorts {
  ProductActionButton: ComponentType<ProductActionButtonProps> | null
  SpecialPromotion: ComponentType<SpecialPromotionProps> | null
  ProductReplaceButton: ComponentType<ProductReplaceButtonProps> | null
  // LR: coupon of the cart reported in product analytics events.
  useCartCouponCode: () => string | undefined
}

const ProductPortsContext = createContext<ProductPorts>({
  ProductActionButton: null,
  SpecialPromotion: null,
  ProductReplaceButton: null,
  useCartCouponCode: () => undefined
})

interface ProductPortsProviderProps {
  ports: ProductPorts
  children: ReactNode
}

export const ProductPortsProvider = ({
  ports,
  children
}: ProductPortsProviderProps) => (
  <ProductPortsContext.Provider value={ports}>
    {children}
  </ProductPortsContext.Provider>
)

const warnMissingProvider = (port: keyof ProductPorts) => {
  if (!isProd) {
    console.warn(`${port} rendered outside ProductPortsProvider.`)
  }
}

export const ProductActionButton = (props: ProductActionButtonProps) => {
  const { ProductActionButton: ActionButton } = useContext(ProductPortsContext)

  if (!ActionButton) {
    warnMissingProvider('ProductActionButton')

    return null
  }

  return <ActionButton {...props} />
}

export const ProductSpecialPromotion = (props: SpecialPromotionProps) => {
  const { SpecialPromotion } = useContext(ProductPortsContext)

  if (!SpecialPromotion) {
    warnMissingProvider('SpecialPromotion')

    return null
  }

  return <SpecialPromotion {...props} />
}

export const ProductReplaceButton = (props: ProductReplaceButtonProps) => {
  const { ProductReplaceButton: ReplaceButton } = useContext(ProductPortsContext)

  if (!ReplaceButton) {
    warnMissingProvider('ProductReplaceButton')

    return null
  }

  return <ReplaceButton {...props} />
}

export const useCartCouponCode = () =>
  useContext(ProductPortsContext).useCartCouponCode()
